import { ExerciseStateMachine } from './ExerciseStateMachine';
import { calculateJointAngle, calculateDistance3D } from './FsmUtils'; // Assuming utils exist

export class ArmAbductionFrontFSM extends ExerciseStateMachine {
  constructor() {
    const config = {
      initialState: 'DOWN',
      initialStateData: {
        // Positional data
        previousAvgWristY: 0,
        previousTimestamp: 0,
        // Movement stability
        previousMovement: { 
          isMovingUp: false,
          isMovingDown: false
        },
        // Timers
        lastDownTime: 0,
        lastUpTime: 0,
        // Flags
        isFirstFrame: true,
        lastRaisingTime: 0,
        maxRangeOfMotion: 0,  // Track maximum ROM achieved during rep
        // Debugging
        debug: {
          lastState: 'INIT',
          transitionAttempts: 0,
          failedConditions: [],
          lastMetrics: {},
          logCounter: 0
        }
      },
      initialFormScores: {
        overall: 100,
        height: 100,      // Range of Motion - How high arms are raised
        symmetry: 100,    // Arms moving together / reaching same height
        extension: 100,   // Arms kept straight (elbow angle)
        control: 100,     // Smoothness/velocity control
        rangeOfMotion: 0  // Initialize ROM for display
      },
      states: {
        'DOWN': { description: 'Arms lowered below shoulder height' },
        'RAISING': { description: 'Arms moving upwards' },
        'UP': { description: 'Arms held at or near shoulder height' },
        'LOWERING': { description: 'Arms moving downwards' }
      },
      transitions: {
        'DOWN': [
          {
            targetState: 'RAISING',
            condition: (metrics, stateData, timestamp) => {
              let reason = "";
              try {
                // More permissive - accept any upward movement, regardless of confidence
                if (metrics.isMovingUp) {
                  const minTimeInDown = 100; // Reduced from 200ms to allow quicker transitions
                  const timeInState = timestamp - stateData.lastDownTime;
                  if (timeInState < minTimeInDown) { reason = `TimeInDown < ${minTimeInDown}`; return false; }
                  
                  return true;
                }
                
                if (!metrics.isMovingUp) { reason = "!MovingUp"; return false; }
                
                return false;
              } catch (error) { console.error("Err DOWN->RAISING:", error); return false; }
              finally { if (reason && Math.random() < 0.05) console.log(`FSM DOWN->RAISING fail: ${reason}`); }
            },
            action: (fsm, metrics, timestamp) => {
              console.log(`ArmAbductionFrontFSM: DOWN -> RAISING`);
              fsm.stateData.lastTransitionTime = timestamp;
              fsm.stateData.lastRaisingTime = timestamp; // Track when we entered raising state
            }
          }
        ],
        'RAISING': [
          {
            targetState: 'UP',
            condition: (metrics, stateData, timestamp) => {
              let reason = "";
              try {
                // Much more permissive - transition to UP if:
                // 1. Arms have been raising for a reasonable amount of time
                // 2. OR arms are in a decent position height-wise
                
                // Time-based approach - if we've been raising for a while, assume we've reached up
                const timeInRaising = timestamp - stateData.lastRaisingTime;
                if (timeInRaising > 800) { // After 800ms of raising, consider it UP
                  console.log("ArmAbductionFrontFSM: RAISING -> UP (time-based)");
                  return true;
                }
                
                // Position-based approach
                if (metrics.avgWristHeight > -0.25) { // Very permissive threshold
                  const minTimeInRaising = 100; // ms to prevent instant transition
                  const timeInState = timestamp - stateData.lastTransitionTime;
                  if (timeInState < minTimeInRaising) { reason = `TimeInRaising < ${minTimeInRaising}`; return false; }
                  
                  console.log("ArmAbductionFrontFSM: RAISING -> UP (position-based)");
                  return true;
                }
                
                return false;
              } catch (error) { console.error("Err RAISING->UP:", error); return false; }
              finally { if (reason && Math.random() < 0.05) console.log(`FSM RAISING->UP fail: ${reason}`); }
            },
            action: (fsm, metrics, timestamp) => {
               console.log(`ArmAbductionFrontFSM: RAISING -> UP`);
               fsm.stateData.lastUpTime = timestamp;
               fsm.stateData.lastTransitionTime = timestamp;
            }
          },
          { // Allow falling back down if movement stops prematurely
            targetState: 'DOWN',
            condition: (metrics, stateData, timestamp) => {
              let reason = "";
              try {
                // Only abort if we're clearly moving down or definitely in down position
                if (metrics.isMovingDown && metrics.significantMovement && metrics.isInDownPosition) {
                  console.log("ArmAbductionFrontFSM: RAISING -> DOWN (clear downward movement)");
                  return true;
                }
                
                // TIMEOUT: if stuck in RAISING for too long (3000ms), go back to DOWN
                const timeInState = timestamp - (stateData.lastRaisingTime || timestamp);
                if (timeInState > 3000) { // Increased from 2000ms to 3000ms
                  console.log("ArmAbductionFrontFSM: RAISING -> DOWN (timeout after 3s)");
                  return true;
                }
                 
                return false;
              } catch (error) { console.error("Err RAISING->DOWN:", error); return false; }
              finally { if (reason && Math.random() < 0.05) console.log(`FSM RAISING->DOWN fail: ${reason}`); }
            },
             action: (fsm, metrics, timestamp) => {
               console.log(`ArmAbductionFrontFSM: RAISING -> DOWN (aborted)`);
               fsm.stateData.lastTransitionTime = timestamp;
               fsm.stateData.lastDownTime = timestamp;
             }
          }
        ],
        'UP': [
          {
            targetState: 'LOWERING',
            condition: (metrics, stateData, timestamp) => {
              let reason = "";
              try {
                // Less strict: Allow transition even with low confidence
                if (metrics.significantMovement && metrics.isMovingDown) {
                  const minTimeInUp = 150; // Hold at the top briefly (reduced from 200)
                  const timeInState = timestamp - stateData.lastUpTime;
                  if (timeInState < minTimeInUp) { reason = `TimeInUp < ${minTimeInUp}`; return false; }
                  
                  return true;
                }
                
                return false;
              } catch (error) { console.error("Err UP->LOWERING:", error); return false; }
              finally { if (reason && Math.random() < 0.05) console.log(`FSM UP->LOWERING fail: ${reason}`); }
            },
             action: (fsm, metrics, timestamp) => {
               console.log(`ArmAbductionFrontFSM: UP -> LOWERING`);
               fsm.stateData.lastTransitionTime = timestamp;
             }
          },
          { // Failsafe timeout
            targetState: 'DOWN',
            condition: (metrics, stateData, timestamp) => {
               const maxTimeInUp = 2000; // Decreased from 3000 to 2000ms
               return timestamp - stateData.lastUpTime > maxTimeInUp;
            },
             action: (fsm, metrics, timestamp) => {
               console.log(`ArmAbductionFrontFSM: UP -> DOWN (timeout)`);
               fsm.stateData.lastTransitionTime = timestamp;
               fsm.stateData.lastDownTime = timestamp;
             }
          }
        ],
        'LOWERING': [
          {
            targetState: 'DOWN',
            condition: (metrics, stateData, timestamp) => {
              let reason = "";
              try {
                // More lenient condition for front raises - detect when arms are close to down position
                if (metrics.isInDownPosition || metrics.avgWristHeight < -0.2) {
                  return true;
                }
                
                // If movement has stopped and we're not in UP position, assume we're done
                if (!metrics.isMovingDown && !metrics.isInUpPosition && metrics.significantMovement) {
                  return true;
                }
                
                // Timeout if we're stuck in LOWERING too long (reduced for better responsiveness)
                const maxTimeInLowering = 1500; // ms (reduced from 2000)
                const timeInState = timestamp - stateData.lastTransitionTime;
                if (timeInState > maxTimeInLowering) {
                  console.log("ArmAbductionFrontFSM: LOWERING -> DOWN (timeout)");
                  return true;
                }
                
                return false;
              } catch (error) { console.error("Err LOWERING->DOWN:", error); return false; }
              finally { if (reason && Math.random() < 0.05) console.log(`FSM LOWERING->DOWN fail: ${reason}`); }
            },
            completesRepetition: true,
            calculateRepScore: (metrics, stateData) => {
              // Use MAX ROM achieved during rep, not current ROM at completion moment
              // heightScore is the same as rangeOfMotion for this exercise
              const maxROM = stateData.maxRangeOfMotion || metrics.heightScore || 0;
              const score = Math.round(
                 (maxROM * 0.4) +
                 (metrics.symmetryScore * 0.3) +
                 (metrics.extensionScore * 0.2) +
                 (metrics.controlScore * 0.1)
              );
              console.log(`ArmAbductionFrontFSM: Completed rep with score ${score}`);
              // Store the calculated score in stateData to be used by updateFormScores
              stateData.repFormScore = score; 
              return score; // Also return for direct use if needed
            },
            action: (fsm, metrics, timestamp) => {
              console.log(`ArmAbductionFrontFSM: LOWERING -> DOWN`);
              fsm.stateData.lastDownTime = timestamp;
              fsm.stateData.lastTransitionTime = timestamp;
            }
          }
        ]
      }
    };
    super(config);
    console.log("ArmAbductionFrontFSM initialized");
  }

  // --- Override methods to implement exercise-specific logic ---

  validatePoseData(poseData) {
     // Add validation logic: Check for shoulders, elbows, wrists confidence
     try {
       const valid = super.validatePoseData(poseData);
       if (!valid) return false;

       const landmarks = poseData.landmarks[0];
       if (!landmarks) return false;

       // Shoulders (11, 12), Elbows (13, 14), Wrists (15, 16)
       const requiredLandmarks = [11, 12, 15, 16]; // Only requiring shoulders and wrists now
       let validCount = 0;
       const minConfidence = 0.2; // Significantly lowered from 0.4 to deal with consistent low confidence

       for (const idx of requiredLandmarks) {
         if (landmarks[idx] && landmarks[idx].visibility > minConfidence) {
           validCount++;
         }
       }
       
       // Require only 2 out of 4 key points (Just need some pair of landmarks)
       const requiredCount = 2; 
       const enoughValid = validCount >= requiredCount;
       if (!enoughValid && Math.random() < 0.05) {
          console.warn(`ArmAbductionFrontFSM: Not enough valid landmarks (${validCount}/${requiredLandmarks.length}), required ${requiredCount} with >${minConfidence} confidence.`);
       }
       return enoughValid;

     } catch (error) {
       console.error("ArmAbductionFrontFSM validatePoseData error:", error);
       return false;
     }
  }

  computeMetrics(landmarks, worldLandmarks, timestamp) {
    try {
      // --- Get Landmarks ---
      const leftShoulder = worldLandmarks[11];
      const rightShoulder = worldLandmarks[12];
      const leftElbow = worldLandmarks[13];
      const rightElbow = worldLandmarks[14];
      const leftWrist = worldLandmarks[15];
      const rightWrist = worldLandmarks[16];

      // --- Confidence Check ---
      const conf = (idx) => landmarks[idx]?.visibility || 0;
      const minConfidence = 0.5; // Lowered from 0.6
      const lowConfidence = conf(11) < minConfidence || conf(12) < minConfidence || 
                           conf(15) < minConfidence || conf(16) < minConfidence; // Only require shoulders and wrists

      // --- Core Metrics ---
      const avgShoulderY = (leftShoulder.y + rightShoulder.y) / 2;
      const avgWristY = (leftWrist.y + rightWrist.y) / 2;
      const avgWristHeight = avgShoulderY - avgWristY; // Higher value means higher raise

      // Safeguard elbow values which might be less reliable
      const safeLeftElbow = leftElbow || { x: (leftShoulder.x + leftWrist.x) / 2, y: (leftShoulder.y + leftWrist.y) / 2, z: (leftShoulder.z + leftWrist.z) / 2 };
      const safeRightElbow = rightElbow || { x: (rightShoulder.x + rightWrist.x) / 2, y: (rightShoulder.y + rightWrist.y) / 2, z: (rightShoulder.z + rightWrist.z) / 2 };
      
      let leftElbowAngle = 160;
      let rightElbowAngle = 160;
      try {
        if (leftElbow && conf(13) > 0.4) {
          leftElbowAngle = calculateJointAngle(leftShoulder, safeLeftElbow, leftWrist);
        }
        if (rightElbow && conf(14) > 0.4) {
          rightElbowAngle = calculateJointAngle(rightShoulder, safeRightElbow, rightWrist);
        }
      } catch (e) {
        console.error("Error calculating elbow angles:", e);
      }
      
      const avgElbowAngle = (leftElbowAngle + rightElbowAngle) / 2;

      // --- Movement & Velocity ---
      let wristVelocity = 0;
      let significantMovement = false;
      let dy = 0; // Change in average Y position

      if (!this.stateData.isFirstFrame && this.stateData.previousTimestamp) {
        const timeDelta = (timestamp - this.stateData.previousTimestamp) / 1000;
        if (timeDelta > 0) {
          dy = avgWristY - this.stateData.previousAvgWristY;
          const distance = Math.abs(dy); // Primarily interested in vertical movement
          significantMovement = distance > 0.003; // Lowered threshold for more sensitive movement detection
          wristVelocity = distance / timeDelta; 
          
          // Prevent NaN or extremely large values
          if (isNaN(wristVelocity) || !isFinite(wristVelocity)) {
            wristVelocity = 0;
          }
        }
      }

      // --- Positional States ---
      const upThreshold = -0.15; // Much more lenient - arms just need to be somewhat raised
      const downThreshold = -0.20; // Less strict down position - arms just need to be somewhat lowered
      const isInUpPosition = avgWristHeight >= upThreshold;
      const isInDownPosition = avgWristHeight < downThreshold;

      // --- Movement Direction (Stabilized) ---
      // Raw direction this frame
      const currentMovementUp = dy < -0.0005; // Extremely sensitive threshold - any upward movement counts
      const currentMovementDown = dy > 0.0005; // Extremely sensitive threshold - any downward movement counts
      
      // Stabilized direction (requires confirmation from previous frame)
      let isMovingUp = false;
      let isMovingDown = false;
      if (currentMovementUp) {
         isMovingUp = true;
      }
      if (currentMovementDown) {
         isMovingDown = true;
      }
      
      // Store raw movement for next frame
      const previousMovement = { isMovingUp: currentMovementUp, isMovingDown: currentMovementDown };

      // --- Form Score Metrics ---
      const optimalHeight = 0.25; // Arms raised to shoulder level (25cm above shoulders)
      const heightScore = Math.min(100, Math.max(0, (avgWristHeight / optimalHeight) * 100));
      
      const yDiff = Math.abs(leftWrist.y - rightWrist.y);
      const angleDiff = Math.abs(leftElbowAngle - rightElbowAngle);
      const symmetryScore = Math.max(0, 100 - (yDiff * 300) - (angleDiff * 1.5)); // Penalize differences

      const extensionScore = Math.min(100, Math.max(0, (avgElbowAngle / 170) * 100)); // Score higher for straighter arms (closer to 180)

      const optimalVelocity = 0.4; // m/s (adjust based on testing)
      const controlScore = Math.max(0, 100 - Math.abs(wristVelocity - optimalVelocity) * 100);

      // --- Logging ---
      if (this.stateData.debug.logCounter === undefined) this.stateData.debug.logCounter = 0;
      this.stateData.debug.logCounter = (this.stateData.debug.logCounter + 1) % 15; // Log less often
      if (this.stateData.debug.logCounter === 0) {
         console.log(
           `ArmAbductionFrontFSM Metrics: State=${this.currentState}, LowConf=${lowConfidence}, SigMov=${significantMovement}, ` +
           `MovingUp=${isMovingUp}(${currentMovementUp}), MovingDown=${isMovingDown}(${currentMovementDown}), ` +
           `InUp=${isInUpPosition}, InDown=${isInDownPosition}, ` +
           `AvgHeight=${avgWristHeight.toFixed(3)}, Vel=${wristVelocity.toFixed(2)}, ElbowAng=${avgElbowAngle.toFixed(1)}`
         );
      }

      return {
        // Positional
        avgWristHeight,
        avgWristY, // Added to store for next frame
        isInUpPosition,
        isInDownPosition,
        // Movement
        significantMovement,
        isMovingUp, // Stabilized
        isMovingDown, // Stabilized
        wristVelocity,
        previousMovement, // Raw movement for next frame's comparison
        // Flags
        lowConfidence,
        // Form Scores
        heightScore,
        symmetryScore,
        extensionScore,
        controlScore,
        rangeOfMotion: heightScore  // Expose heightScore as rangeOfMotion for consistency
      };

    } catch (error) {
      console.error("ArmAbductionFrontFSM computeMetrics error:", error);
      // Return safe defaults
      return {
        avgWristHeight: -0.5, avgWristY: 0, isInUpPosition: false, isInDownPosition: true,
        significantMovement: false, isMovingUp: false, isMovingDown: false, wristVelocity: 0,
        previousMovement: { isMovingUp: false, isMovingDown: false },
        lowConfidence: true, heightScore: 0, symmetryScore: 0, extensionScore: 0, controlScore: 0
      };
    }
  }

  updateStateData(metrics, timestamp) {
     // Update state data with previous values needed for next frame calculation
     try {
        this.stateData = {
           ...this.stateData,
           previousAvgWristY: metrics.avgWristY,
           previousTimestamp: timestamp,
           previousMovement: {
              isMovingUp: metrics.isMovingUp,
              isMovingDown: metrics.isMovingDown
           },
           isFirstFrame: false,
           lastTransitionTime: this.stateData.lastTransitionTime || timestamp // Keep from checkTransitions
        };
        
        // Track maximum ROM achieved during RAISING and UP (while arms are elevated, not during lowering)
        if (this.currentState === 'RAISING' || this.currentState === 'UP') {
          this.stateData.maxRangeOfMotion = Math.max(
            this.stateData.maxRangeOfMotion || 0,
            metrics.rangeOfMotion || 0
          );
        }
     } catch (error) {
        console.error("ArmAbductionFrontFSM updateStateData error:", error);
     }
  }

  updateFormScores(metrics, transitioned) {
     // Update overall form score based on metrics, potentially weighted by rep completion
      try {
         const scores = this.formScores;
         const repScore = this.stateData.repFormScore; // Calculated in transition

         // Make sure we don't assign NaN values to UI display
         scores.height = isNaN(metrics.heightScore) ? 0 : metrics.heightScore;
         scores.symmetry = isNaN(metrics.symmetryScore) ? 0 : metrics.symmetryScore;
         scores.extension = isNaN(metrics.extensionScore) ? 0 : metrics.extensionScore;
         scores.control = isNaN(metrics.controlScore) ? 0 : metrics.controlScore;
         
         // ROM: Update rangeOfMotion for display (same as height score)
         scores.rangeOfMotion = Math.round(metrics.rangeOfMotion || 0);

         if (repScore) {
            // Blend overall score with the score from the last completed rep
            scores.overall = Math.round(scores.overall * 0.7 + repScore * 0.3);
            this.stateData.repFormScore = 0; // Reset after use
         } else {
             // Continuous update based on current metrics during movement
             const currentFrameScore = Math.round(
                (scores.height * 0.4) +
                (scores.symmetry * 0.3) +
                (scores.extension * 0.2) +
                (scores.control * 0.1)
             );
             // Faster update when not completing a rep
             scores.overall = Math.round(scores.overall * 0.7 + currentFrameScore * 0.3);
         }
         this.formScores = scores;
      } catch(error) {
         console.error("ArmAbductionFrontFSM updateFormScores error:", error);
      }
  }

  generateFormFeedback(metrics, currentState) {
     // Provide feedback based on current state and metrics
     try {
       if (metrics.lowConfidence) return "Tracking lost, check camera view.";

       // Feedback during movement
       if (currentState === 'RAISING' || currentState === 'LOWERING') {
          if (metrics.symmetryScore < 75) return "Keep arms moving evenly.";
          if (metrics.extensionScore < 80) return "Keep arms straighter.";
          if (metrics.controlScore < 70) return "Move slower and more controlled.";
       }
       
       // Feedback at the top
       if (currentState === 'UP') {
          if (metrics.heightScore < 80) return "Raise arms higher (to shoulder level).";
          if (metrics.symmetryScore < 80) return "Ensure arms are at the same height.";
          if (metrics.extensionScore < 85) return "Keep arms straight at the top.";
       }
       
       // Feedback if aborted
       if (currentState === 'DOWN' && metrics.isMovingDown) { // Means likely aborted RAISING
          // No specific feedback needed here unless we track reason for abort
       }

       return null; // No feedback
     } catch(error) {
        console.error("ArmAbductionFrontFSM generateFormFeedback error:", error);
        return null;
     }
  }

  checkTransitions(metrics, timestamp) {
    try {
      // Store the current state before transitions
      const prevState = this.currentState;
      
      // Possibly force transition if stuck too long in a state
      if (this.currentState === 'RAISING') {
        const stuckTime = timestamp - (this.stateData.lastRaisingTime || timestamp);
        if (stuckTime > 2500) { // Stuck over 2.5s
          console.log("FSM force reset - stuck in RAISING too long");
          this.currentState = 'DOWN';
          this.stateData.lastDownTime = timestamp;
          this.stateData.lastTransitionTime = timestamp;
          return true;
        }
      }
      
      // Call the parent implementation for state machine logic
      const didTransition = super.checkTransitions(metrics, timestamp);
      
      // Ensure lastTransitionTime is set when a transition occurs
      if (didTransition) {
        this.stateData.lastTransitionTime = timestamp;
      }
      
      return didTransition;
    } catch (error) {
      console.error("ArmAbductionFrontFSM checkTransitions error:", error);
      return false;
    }
  }
} 